import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";

import { LayoutFolderGroup } from "./layoutFolders";
import { useBulkLayoutMutation } from "./useBulkLayoutMutation";

interface DeleteFolderConfirmationProps {
  group: LayoutFolderGroup | null;
  isOpen: boolean;
  onClose: () => void;
  onExited: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

export function DeleteFolderConfirmation({
  group,
  isOpen,
  onClose,
  onExited,
  onSuccess,
}: DeleteFolderConfirmationProps) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ onExited }}
      aria-labelledby="delete-folder-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      {group && group.folder !== null && (
        <DeleteFolderBody
          group={group}
          folder={group.folder}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}

interface DeleteFolderBodyProps {
  group: LayoutFolderGroup;
  folder: string;
  onClose: () => void;
  onSuccess: (updated: LayoutRecord[]) => void;
}

function DeleteFolderBody({
  group,
  folder,
  onClose,
  onSuccess,
}: DeleteFolderBodyProps) {
  const updateLayout = useUpdateLayout();
  const { run, isPending, errorMessage } = useBulkLayoutMutation(
    "Failed to delete folder. Please try again.",
  );

  const count = group.layouts.length;

  const handleConfirm = () => {
    run(
      group.layouts,
      (option) =>
        updateLayout.mutateAsync({
          layoutId: option.id,
          updates: { folder: null },
        }),
      () =>
        onSuccess(
          group.layouts.map((option) => ({ ...option.record, folder: null })),
        ),
    );
  };

  return (
    <>
      <DialogTitle id="delete-folder-dialog-title">Delete folder</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body1">
            Delete the folder <b>&quot;{folder}&quot;</b>? Its {count}{" "}
            {count === 1 ? "layout" : "layouts"} will move to Ungrouped. The
            layouts themselves are not deleted.
          </Typography>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <RobotoButton
          eventName="DeleteFolderCanceled"
          eventProperties={{ folder }}
          onClick={onClose}
          type="button"
          disabled={isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="DeleteFolderClicked"
          eventProperties={{ folder }}
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isPending}
          endIcon={
            isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Delete folder
        </RobotoButton>
      </DialogActions>
    </>
  );
}
